import { Component, Output, EventEmitter, Input, ChangeDetectionStrategy, OnInit, AfterViewInit } from '@angular/core';
import { BarChartDataItem, BarChartYAxis, ChartWidgetLabelSettings, barChartPointRadius } from '../chart.models';
import DataLabelsPlugin from 'src/app/modules/communications/components/notification/charts/plugins/data-labels';
import { CultureService } from 'src/app/modules/core/services/culture.service';

@Component({
    selector: 'topo-bar-chart',
    templateUrl: './bar-chart.component.html',
    styleUrls: ['./bar-chart.component.scss']
    //  changeDetection: ChangeDetectionStrategy.OnPush
    ,
    standalone: false
})
export class BarChartComponent {
  constructor(private cultureService: CultureService) {}
  chartType: 'bar';

  chartData: {
    labels: string[] | undefined[];
    datasets: Array<{ type: string; data: number[]; backgroundColor: string[]; order: number }[] | undefined[]>;
  };

  private pointRadius = barChartPointRadius;
  private maxValue: any;
  public barChartPlugins = [DataLabelsPlugin];

  @Input() width: string;
  @Input() height: string;
  @Input() horizontal: boolean;
  @Input() responsive: boolean;
  @Input() xGap: number = 1;

  @Input() types: Array<'bar' | 'line'>;
  @Input() yAxis: Array<BarChartYAxis>;
  @Input() xAxis: Array<BarChartYAxis>;
  @Input() dataLabel?: string;
  @Input() countLabel?: string;
  @Input() colorPerBar?: boolean;
  @Input() colors?: any;
  @Input() dataLabelDisplay?: ChartWidgetLabelSettings;
  @Input() set data(value: Array<BarChartDataItem>) {
    this._data = value;

    if (value && value.length > 0) {
      const tempData = {
        labels: value.map(v => v.label),
        datasets: []
      };

      let index = 0;

      this.types?.forEach(type => {
        const dataset = {
          type: type,
          data: value.map(v => v.data[index].count),
          order: index + 1,
          total: 0
        };

        let total = 0;
        value.map(v => total += v.data[index].count);
        dataset.total = total;

        this.maxValue = 0;
        value.map(v => { if (v.data[index].count >  this.maxValue) this.maxValue = v.data[index].count });

        const color = value[0].data[index].color !== null ? value[0].data[index].color : undefined;

        if (this.yAxis && this.yAxis.length - 1 >= index && !!this.yAxis[index]) {
          dataset['yAxisID'] = this.yAxis[index].id;
        }

        if (this.xAxis && this.xAxis.length - 1 >= index && !!this.xAxis[index]) {
          dataset['xAxisID'] = this.yAxis[index].id;
        }

        if (type === 'bar') {
          if (this.colorPerBar === true) {
            dataset['backgroundColor'] = value.some(v => v.data[0].color != null) ? value.map(v => v.data[0].color) : undefined;
          }
          else {
            dataset['backgroundColor'] = color;
          }
          dataset['borderRadius'] = {
            topLeft: 6, topRight: 6, bottomLeft: 0, bottomRight: 0
          };
        } else {
          if (this.colorPerBar === true) {
            const barColor = value.some(v => v.data[0].color != null) ? value.map(v => v.data[0].color) : undefined;
            dataset['borderColor'] = "#808080";
            dataset['borderWidth'] = 2;
            dataset['pointBackgroundColor'] = barColor;
          }
          else {
            dataset['borderColor'] = "#808080";
            dataset['borderWidth'] = 2;
            dataset['pointBackgroundColor'] = color;
          }
          dataset['pointRadius'] = this.pointRadius;
          dataset['fill'] = false;
        }

        index++;
        tempData.datasets.push(dataset);
      });

      this.chartData = tempData;
    } else {
      this.chartData = {
        labels: [],
        datasets: []
      };
    }
  }

  @Output() select = new EventEmitter<{ item: BarChartDataItem, subValue: any }>();

  get chartOptions(): any {
    if (!this.horizontal) {
      return {
        responsive: true,
        animation: false,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false,
          },
          datalabels: {
            display: !!this.dataLabelDisplay?.displayValues || !!this.dataLabelDisplay?.displayPercentage,
            anchor: 'end',
            align: 'top',
            color: this.colors?.labelColor,
            formatter: (value, context) => {
              return this.getLabelDisplay(value, context);
            },
            offset: 4
          }
        },
        scales: {
          y: {
            type: 'linear',
            position: 'left',
            beginAtZero: true,
            grid: {
              color: this.colors?.axisColor,
              display: true
            },
            min: 0,
            ticks: {
              color: this.colors?.titleColor,
              callback: (val, index) => {
                return (this.chartData?.datasets[0] as any)?.total > 0 || val === 0 ? val : undefined;
              },
              stepSize: 1
            },
            title: { display: true, text: this.countLabel, color: this.colors?.titleColor }
          },
          x: {
            offset: true,
            beginAtZero: false,
            grid: {
              color: this.colors?.axisColor,
              display: true,
              drawOnChartArea: false,
              drawTicks: true,
              offset: false
            },
            ticks: {
              color: this.colors?.titleColor,
              callback: (val, index) => {
                return index % this.xGap === 0 ? this.chartData.labels[index] : '';
              },
              stepSize: 1,
              display: true
            },
            title: { display: true, text: this.dataLabel, color: this.colors?.titleColor }
          }
        }
      };
    } else {
      return {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false
          },
          datalabels: {
            display: !!this.dataLabelDisplay?.displayValues || !!this.dataLabelDisplay?.displayPercentage,
            anchor: 'end',
            align: 'end',
            color: this.colors?.labelColor,
            clamp: true,
            clip: true,
            formatter: (value, context) => {
              return this.getLabelDisplay(value, context);
            },
            offset: 4,
          }
        },
        scales: {
          x: {
            type: 'linear',
            position: 'bottom',
            beginAtZero: true,
            offset: false,
            suggestedMax: () => {
              return this.maxValue * 1.5;
            },
            grid: {
              color: this.colors?.axisColor,
            },
            ticks: {
              color: this.colors?.titleColor,
            },
            title: { display: true, text: this.countLabel, color: this.colors?.titleColor }
          },
          y: {
            offset: true,
            grid: {
              color: this.colors?.axisColor,
            },
            ticks: {
              color: this.colors?.titleColor,
            },
            title: { display: true, text: this.dataLabel, color: this.colors?.titleColor }
          }
        }
      };
    }
  }

  private _data: Array<BarChartDataItem>;

  chartClick(event: any): void {
    if (!!event.active[0]) {
      this.select.emit({ item: this._data[event.active[0].index], subValue: undefined });
    }
  }

  private getLabelDisplay(value, context): string {
    let result = '';
    if (this.dataLabelDisplay?.displayValues) {
      result = this.cultureService.getCurrencyDisplay(value, 0);
    }
    if (this.dataLabelDisplay?.displayPercentage) {
      const val = (value / context.dataset.total) * 100;
      if (!!result) {
        result = `${result} (${this.cultureService.getCurrencyDisplay(val)}%)`;
      }
      else {
        result = `${this.cultureService.getCurrencyDisplay(val)}%`;
      }
    }
    return result;
  }
}
